import * as React from 'react';
import { createContext, useContext } from 'react';

import useOnboardingV3 from 'bundles/onboarding-common/hooks/useOnboardingV3';

export type OnboardingV3ContextType = {
  isEligibleForOnboardingV3: boolean;
  getShouldShowOnboardingV3: () => boolean;
  getShouldShowOnboardingV3Variant1: () => boolean;
  getShouldShowOnboardingV3Variant2: () => boolean;
  getOnboardingV3Variant: () => string;
  impressIntoOnboardingV3WithEligibilityCheck: () => void;
  loading: boolean;
};

const OnboardingV3Context = createContext<OnboardingV3ContextType | null>(null);

type OnboardingV3ProviderProps = {
  children: React.ReactNode;
};

const OnboardingV3Provider = ({ children }: OnboardingV3ProviderProps) => {
  const {
    isEligibleForOnboardingV3,
    getShouldShowOnboardingV3,
    getShouldShowOnboardingV3Variant1,
    getShouldShowOnboardingV3Variant2,
    getOnboardingV3Variant,
    impressIntoOnboardingV3WithEligibilityCheck,
    loading,
  } = useOnboardingV3();

  const contextValue: OnboardingV3ContextType = {
    isEligibleForOnboardingV3,
    getShouldShowOnboardingV3,
    getShouldShowOnboardingV3Variant1,
    getShouldShowOnboardingV3Variant2,
    getOnboardingV3Variant,
    impressIntoOnboardingV3WithEligibilityCheck,
    loading,
  };

  return <OnboardingV3Context.Provider value={contextValue}>{children}</OnboardingV3Context.Provider>;
};

/**
 * Hook to use OnboardingV3 context
 * Provides consistent onboarding V3 eligibility and variant checking across components
 */
export const useOnboardingV3Context = (): OnboardingV3ContextType => {
  const context = useContext(OnboardingV3Context);
  if (!context) {
    throw new Error('useOnboardingV3Context must be used within an OnboardingV3Provider');
  }
  return context;
};

export default OnboardingV3Provider;
